import React, { useEffect, useState } from 'react';
import {
  Gamepad2, Loader2, Search, Eye, ArrowLeft, ChevronRight,
  Star, Download, DollarSign, Calendar, User as UserIcon,
} from 'lucide-react';
import api from '../../lib/api';

interface AdminGame {
  id: string;
  title: string;
  slug: string;
  description: string;
  developer_name: string;
  developer_id: string;
  price: number;
  download_count: number;
  cover_image_url: string | null;
  banner_image_url: string | null;
  is_published: boolean;
  average_rating: number | null;
  review_count: number;
  current_version: string | null;
  created_at: string;
  updated_at: string;
  reviews?: GameReview[];
}

interface GameReview {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  rating: number;
  content: string;
  created_at: string;
}

interface ReviewItem {
  id: string;
  game_id: string;
  game_title: string;
  version: string;
  review_status: string;
  submitted_at: string;
  developer_name: string;
}

const AdminGames: React.FC = () => {
  const [games, setGames] = useState<AdminGame[]>([]);
  const [reviewLibrary, setReviewLibrary] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'games' | 'review'>('games');
  const [search, setSearch] = useState('');
  const [selectedGame, setSelectedGame] = useState<AdminGame | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchGames = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/games?limit=100');
      setGames(data.games || data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const fetchReview = async () => {
    try {
      const { data } = await api.get('/admin/review-library');
      setReviewLibrary(data.items || data);
    } catch { /* silent */ }
  };

  const fetchGameDetail = async (id: string) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/games/${id}`);
      setSelectedGame(data);
    } catch { /* silent */ }
    setLoadingDetail(false);
  };

  useEffect(() => { fetchGames(); fetchReview(); }, []);

  const filteredGames = games.filter((g) =>
    g.title.toLowerCase().includes(search.toLowerCase()) ||
    g.developer_name?.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Game detail view ── */
  if (selectedGame) {
    const g = selectedGame;
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-5">
          <button onClick={() => setSelectedGame(null)} className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour à la liste
          </button>

          {/* Header with banner */}
          <div className="relative rounded-xl overflow-hidden">
            {g.banner_image_url ? (
              <img src={g.banner_image_url} className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 bg-gradient-to-r from-gaming-accent/20 to-admin-accent/20" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 flex items-end gap-4">
              {g.cover_image_url ? (
                <img src={g.cover_image_url} className="w-20 h-20 rounded-xl object-cover border-2 border-white/20 shadow-lg" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-gaming-accent/20 flex items-center justify-center border-2 border-white/20"><Gamepad2 className="h-8 w-8 text-gaming-accent" /></div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white">{g.title}</h2>
                  {!g.is_published && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-warning/20 text-warning">Non publié</span>}
                </div>
                <p className="text-xs text-white/60 mt-0.5">{g.developer_name} · v{g.current_version || '?'}</p>
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { icon: DollarSign, label: 'Prix', value: Number(g.price) === 0 ? 'Gratuit' : `${Number(g.price).toFixed(2)} $` },
              { icon: Download, label: 'Téléchargements', value: String(g.download_count || 0) },
              { icon: Star, label: 'Note moyenne', value: g.average_rating ? `${Number(g.average_rating).toFixed(1)} / 5` : 'N/A' },
              { icon: Calendar, label: 'Créé', value: new Date(g.created_at).toLocaleDateString('fr-CA') },
            ].map((s) => (
              <div key={s.label} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-3">
                <div className="flex items-center gap-1.5 text-xs text-text-muted mb-1"><s.icon className="h-3.5 w-3.5" />{s.label}</div>
                <p className="text-sm font-semibold text-text-primary">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          {g.description && (
            <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Description</p>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{g.description}</p>
            </div>
          )}

          {/* Reviews */}
          <div className="bg-bg-secondary/60 rounded-xl border border-border-color/50 p-4">
            <p className="text-xs text-text-muted uppercase tracking-wider mb-3">Avis ({g.reviews?.length || 0})</p>
            {(!g.reviews || g.reviews.length === 0) ? (
              <p className="text-sm text-text-muted">Aucun avis pour ce jeu</p>
            ) : (
              <div className="space-y-3">
                {g.reviews.map((r) => (
                  <div key={r.id} className="flex gap-3">
                    {r.avatar_url ? (
                      <img src={r.avatar_url} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-admin-accent/10 flex items-center justify-center text-[10px] font-bold text-admin-accent flex-shrink-0">
                        {(r.username || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-text-primary">{r.username}</span>
                        <span className="text-warning flex items-center gap-0.5">
                          <Star className="h-3 w-3 fill-current" /> {r.rating}
                        </span>
                        <span className="text-text-muted">{new Date(r.created_at).toLocaleDateString('fr-CA')}</span>
                      </div>
                      {r.content && <p className="text-xs text-text-secondary mt-1">{r.content}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── List view ── */
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Jeux & Store</h2>
            <p className="text-xs text-text-muted mt-0.5">Gestion des jeux et bibliothèque de revue</p>
          </div>
          <div className="flex gap-1 bg-bg-tertiary/50 rounded-lg p-0.5">
            <button onClick={() => setTab('games')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'games' ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted'}`}>
              Jeux ({games.length})
            </button>
            <button onClick={() => setTab('review')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'review' ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted'}`}>
              En revue ({reviewLibrary.length})
            </button>
          </div>
        </div>

        {tab === 'games' && (
          <>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un jeu..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-admin-accent/50"
              />
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-admin-accent" /></div>
            ) : filteredGames.length === 0 ? (
              <div className="text-center py-16">
                <Gamepad2 className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
                <p className="text-sm text-text-muted">Aucun jeu trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-1">
                {filteredGames.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => fetchGameDetail(g.id)}
                    className="w-full flex items-center gap-3 bg-bg-secondary/40 rounded-xl px-4 py-3 hover:bg-bg-secondary/60 transition-colors text-left"
                  >
                    {g.cover_image_url ? (
                      <img src={g.cover_image_url} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gaming-accent/10 flex items-center justify-center"><Gamepad2 className="h-5 w-5 text-gaming-accent" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{g.title}</p>
                      <p className="text-xs text-text-muted">{g.developer_name} · {g.download_count} téléchargements</p>
                    </div>
                    <span className="text-sm font-semibold text-text-primary tabular-nums">{Number(g.price) === 0 ? 'Gratuit' : `${Number(g.price).toFixed(2)} $`}</span>
                    <ChevronRight className="h-4 w-4 text-text-muted/40" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'review' && (
          <>
            {reviewLibrary.length === 0 ? (
              <div className="text-center py-16">
                <Eye className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
                <p className="text-sm text-text-muted">Aucun jeu en revue</p>
              </div>
            ) : (
              <div className="space-y-2">
                {reviewLibrary.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-bg-secondary/40 rounded-xl px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">{r.game_title} <span className="text-text-muted text-xs">v{r.version}</span></p>
                      <p className="text-xs text-text-muted">{r.developer_name} · Soumis {new Date(r.submitted_at).toLocaleDateString('fr-CA')}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      r.review_status === 'pending' ? 'bg-warning/10 text-warning' : r.review_status === 'approved' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                    }`}>
                      {r.review_status === 'pending' ? 'En attente' : r.review_status === 'approved' ? 'Approuvé' : 'Rejeté'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminGames;
